import type { EhrConnector } from "@elation/api-client/src/stitch-web";
import type { ActionItemCategory } from "@elation/api-client/src/stitch-web/models/actionItemCategory";

import { defineEvent } from "./defineEvent";

type IframeLoginMethod = `iframe_${EhrConnector}`;

export type LoginMethod =
  | "okta_web"
  | "okta_mobile"
  | "email"
  | IframeLoginMethod;

/**
 * Single source of truth for every Stitch analytics event.
 *
 * Adding an event = adding an entry here. Call sites use
 * `events.<eventName>.track({ ... })` and TypeScript enforces the property
 * shape. Naming and property conventions are documented in
 * `apps/web/docs/analytics.md`.
 */

const SOURCE_VALUES = [
  "patient list",
  "agenda",
  "encounter",
  "nav",
  "iframe",
  "deep link",
] as const;

/** Where a user-initiated action originated. */
export type Source = (typeof SOURCE_VALUES)[number];

/**
 * Narrows an unknown value into a known `Source`. Reads the `source` field
 * from `location.state`-shaped objects (or any object with a `source` key)
 * and validates it against the allowed `Source` union. Falls back to `"nav"`
 * when the value is missing, malformed, or unrecognized.
 */
export function parseSource(state: unknown, fallback: Source = "nav"): Source {
  if (state == null || typeof state !== "object") return fallback;
  const raw = (state as { source?: unknown }).source;
  return (SOURCE_VALUES as readonly unknown[]).includes(raw)
    ? (raw as Source)
    : fallback;
}

export type Success = { success: true };
export type Failure = {
  success: false;
  error: string;
  error_code?: string;
  error_source?: string;
};
/**
 * Discriminated union for events that represent a flow which can fail.
 * Forces callers to supply either `success: true` or the error fields.
 */
export type Outcome = Success | Failure;

export const events = {
  // Auth / session
  userLoggedIn: defineEvent<{ method: LoginMethod }>("user logged in"),
  userLoggedOut: defineEvent<{
    reason: "manual" | "session_expired" | "auth_error";
  }>("user logged out"),
  loginFailed: defineEvent<{
    method: LoginMethod;
    error: string;
    error_code?: string;
  }>("login failed"),
  startSession: defineEvent("start session"),

  // Patient navigation
  openPatient: defineEvent<{ patient_id: number; source: Source }>(
    "open patient",
  ),
  openAgenda: defineEvent("open agenda"),

  // Action item completion (Today page checkbox)
  actionItemCompleted: defineEvent<{
    action_item_id: number;
    category: ActionItemCategory;
  }>("action item completed"),
  actionItemReopened: defineEvent<{
    action_item_id: number;
    category: ActionItemCategory;
  }>("action item reopened"),
  openDashboard: defineEvent("open dashboard"),

  // Encounter lifecycle (wired into the XState machine)
  startEncounter: defineEvent<{
    encounter_id: number;
    patient_id: number;
  }>("start encounter"),
  encounterInProgress: defineEvent<{
    encounter_id: number;
    patient_id: number;
  }>("encounter in progress"),
  finalizeEncounter: defineEvent<{ encounter_id: number; patient_id: number }>(
    "finalize encounter",
  ),
  completeEncounter: defineEvent<
    { encounter_id: number; patient_id: number; duration_ms?: number } & Outcome
  >("complete encounter"),
  reopenEncounter: defineEvent<{
    encounter_id: number;
    patient_id: number;
    new_encounter_id?: number;
  }>("reopen encounter"),

  // Encounter actions
  copyNote: defineEvent<{ encounter_id: number; patient_id: number } & Outcome>(
    "copy note",
  ),
  viewTranscript: defineEvent<{ encounter_id: number; patient_id: number }>(
    "view transcript",
  ),
  exportNote: defineEvent<
    {
      encounter_id: number;
      patient_id: number;
      destination: "clipboard" | "ehr";
    } & Outcome
  >("export note"),

  // AI suggestion / feature acceptance (generic, `feature` is the discriminator)
  openSuggestion: defineEvent<{
    feature: string;
    suggestion_id: string;
    encounter_id?: number;
    patient_id?: number;
  }>("open suggestion"),
  acceptSuggestion: defineEvent<{
    feature: string;
    suggestion_id: string;
    encounter_id?: number;
    patient_id?: number;
  }>("accept suggestion"),
  dismissSuggestion: defineEvent<{
    feature: string;
    suggestion_id: string;
    encounter_id?: number;
    patient_id?: number;
    reason?: string;
  }>("dismiss suggestion"),

  // Document upload → care-gap extraction funnel
  documentUploaded: defineEvent<{ patient_id: number } & Outcome>(
    "document uploaded",
  ),
  careGapsExtracted: defineEvent<{ patient_id: number; count: number }>(
    "care gaps extracted",
  ),

  // Iframe / Chrome extension
  iframeInitialized: defineEvent<{ parent_origin: string }>(
    "iframe initialized",
  ),
  iframePatientSync: defineEvent<{ patient_id?: number }>(
    "iframe patient sync",
  ),

  // Generic failure catch-all
  appError: defineEvent<{
    surface: string;
    error: string;
    error_code?: string;
    error_source?: string;
    fatal: boolean;
  }>("app error"),
} as const;
